import React, { useCallback, useEffect, useMemo, useRef } from "react";

import TableCell from "./TableCell";
import { COLUMN_WIDTHS } from "../constants";

import { Trash2Icon } from "lucide-react";

// Static styles moved outside component to prevent recreation
const rowStyles = {
  container:
    "flex items-stretch border-b border-gray-200 hover:bg-gray-50 min-h-[60px] min-w-max",
  cell: "px-3 py-2 text-sm text-gray-700 flex items-center border-r border-gray-100 last:border-r-0 overflow-hidden flex-shrink-0",
  cellError: "text-xs text-red-600 h-4 mt-0.5 truncate",
  actionCell:
    "px-3 py-2 text-center flex items-center justify-center flex-shrink-0",
  deleteButton: "text-red-500 hover:text-red-700",
  rowNumber: "px-3 py-2 text-sm text-gray-500 flex items-center justify-center border-r border-gray-100 flex-shrink-0"
};

const TableRow = React.memo(
  ({
    index,
    style,
    item,
    columnMapping,
    columnWidths = {},
    handleCellChange,
    deleteRow,
    validation = {},
  }) => {
    const rowRef = useRef(null);
    const cellRefs = useRef({});

    // Memoize the cell change handler to prevent unnecessary re-renders
    const handleCellChangeMemoized = useCallback(
      (key, value) => {
        handleCellChange(index, key, value);
      },
      [handleCellChange, index],
    );

    // Memoize the delete row handler
    const handleDeleteRow = useCallback(() => {
      deleteRow(index);
    }, [deleteRow, index]);

    // Register cell refs for error focusing
    const registerCellRef = useCallback((key, ref) => {
      cellRefs.current[key] = ref;
    }, []);

    // Get error for a specific cell
    const getCellError = useCallback(
      (key) => {
        if (!validation?.errors || !Array.isArray(validation.errors))
          return null;
        const rowError = validation.errors.find(
          (err) => err?.rowIndex === index,
        );
        if (!rowError?.errors) return null;
        const error = rowError.errors.find((err) => err?.key === key);
        return error?.message || null;
      },
      [validation?.errors, index],
    );

    // Focus the first error cell when validation errors change
    const focusErrorCell = useCallback(() => {
      if (
        validation.firstErrorRef?.current &&
        validation.firstErrorRef.current.rowIndex === index
      ) {
        const errorKey = validation.firstErrorRef.current.columnKey;
        const errorCellRef = cellRefs.current[errorKey];

        if (errorCellRef?.current) {
          try {
            errorCellRef.current.focus();
          } catch (err) {
            console.error("Error focusing cell:", err);
          }
        }
      }
    }, [index, validation.firstErrorRef]);

    // Schedule focus when first error reference changes
    useEffect(() => {
      if (
        validation.firstErrorRef?.current &&
        validation.firstErrorRef.current.rowIndex === index
      ) {
        requestAnimationFrame(focusErrorCell);
      }
    }, [focusErrorCell, index, validation.firstErrorRef]);

    // Memoize column entries to prevent recalculation on every render
    const columnEntries = useMemo(
      () => columnMapping ? Object.entries(columnMapping) : [],
      [columnMapping],
    );

    // Check if row is read-only
    const isReadOnly = validation.isReadOnly ?? false;

    // Combine style props
    const combinedStyle = useMemo(
      () => ({
        ...style,
        backgroundColor: index % 2 === 0 ? "#f8fafc" : "white",
        minHeight: "60px",
      }),
      [style, index],
    );

    // Ensure cell has a value to prevent layout shifts
    const getCellValue = useCallback(
      (key) => {
        const value = item[key];
        return value === null || value === undefined ? "" : value;
      },
      [item],
    );

    return (
      <div
        ref={rowRef}
        className={rowStyles.container}
        style={combinedStyle}
        data-testid={`table-row-${index}`}
      >
        <div 
          className={rowStyles.rowNumber} 
          style={{ 
            width: `${COLUMN_WIDTHS.ROW_NUMBER_WIDTH}px`, 
            minWidth: `${COLUMN_WIDTHS.ROW_NUMBER_WIDTH}px` 
          }}
        >
          {index + 1}
        </div>
        {columnEntries.map(([key, column]) => {
          const width = columnWidths[key] || COLUMN_WIDTHS.DEFAULT_WIDTH;
          return (
            <div
              key={key}
              className={`${rowStyles.cell} ${getCellError(key) ? "bg-red-50 border-red-200" : ""}`}
              style={{
                width: `${width}px`,
                minWidth: `${width}px`,
              }}
            >
              <TableCell
                value={getCellValue(key)}
                onChange={handleCellChangeMemoized}
                error={getCellError(key)}
                ref={(ref) => registerCellRef(key, ref)}
                isEditing={!isReadOnly}
                columnKey={key}
              />
            </div>
          );
        })}
        {!isReadOnly && (
          <div 
            className={rowStyles.actionCell} 
            style={{ 
              width: `${COLUMN_WIDTHS.ACTION_WIDTH}px`, 
              minWidth: `${COLUMN_WIDTHS.ACTION_WIDTH}px` 
            }}
          >
            <button
              onClick={handleDeleteRow}
              className={rowStyles.deleteButton}
              disabled={validation.isSubmitting}
              aria-label="Satırı sil"
              type="button"
            >
              <Trash2Icon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  },
  // Custom comparison function for React.memo
  (prevProps, nextProps) => {
    // Only re-render if necessary props change
    return (
      prevProps.index === nextProps.index &&
      prevProps.style === nextProps.style &&
      prevProps.item === nextProps.item &&
      prevProps.columnMapping === nextProps.columnMapping &&
      prevProps.columnWidths === nextProps.columnWidths &&
      prevProps.validation === nextProps.validation &&
      prevProps.handleCellChange === nextProps.handleCellChange &&
      prevProps.deleteRow === nextProps.deleteRow
    );
  },
);

export default TableRow;
